import type { GeoJsonObject, MultiPolygon, Position } from 'geojson';
import { geoJSON } from 'leaflet';
import { Marker, Popup } from 'react-leaflet';
import cloneDeep from 'lodash/cloneDeep';
import Map from '../Shared/Map';
import './style.scss';
import useFetch, { Status } from '../../hooks/fetch.hook';
import { StopAreaApiModel } from '../StopAreas/models/stop-area.api-model';
import type { IStopArea } from '../../models/stop-area.model';
import { ButtonDivIcon } from '../Shared/Map/markers/button-div-icon';
import Colors from '../../helpers/colors.helper';
import { IServerSideRendered } from '../Shared/Base/server-side-rendered';
import { t } from '../../services/texts.service';
import LocationAutocomplete, { ILocationAutocompleteSelection, ILocationAutocompleteState } from '../Shared/LocationAutocomplete';
import { LocationType } from '../../models/location-type.enum';
import ValidationInfo from '../../validation/validation-info';
import { useState, useMemo } from 'react';
import { AutocompleteLabelPosition } from '../Shared/AutocompleteBase/autocomplete-base.enum';
import { LocationAutocompleteSelectionType } from '../Shared/LocationAutocomplete/location-autocomplete-selection-type.enum';
import { ILocation } from '../../models/location';
import { point } from '@turf/helpers';
import { booleanPointInPolygon } from '@turf/boolean-point-in-polygon';
import { SanitizerService } from '../../services/sanitizer.service';
import stopAreaIcon from '../../../assets/icons/map-marker-vasttrafik-blue-1000.svg';
import localServiceMapIcon from '../../../assets/icons/map-marker-local-service-blue-800.svg';
import exclamationCircleRedIcon from '../../../assets/icons/exclamation-circle-red-600.svg';
import DataUrlHelper from '../../helpers/data-url.helper';
import IBounds from '../Shared/Map/models/bounds.model';
import L from 'leaflet';

export type ILocalServiceMapTexts = object;

export interface ILocalServiceMapSettings {
  municipalityGid: string;
}

type ILocalServiceMapProps = IServerSideRendered<ILocalServiceMapTexts, ILocalServiceMapSettings>;

function processReceivedStopAreaData(data: StopAreaApiModel[] | undefined): IStopArea[] | undefined {
  return data?.map(StopAreaApiModel.mapToBusinessModel) ?? undefined;
}

function getBounds(location?: ILocation, polygon?: GeoJSON.MultiPolygon): IBounds | undefined {
  if (location && location.latitude && location.longitude) {
    return { bounds: new L.LatLngBounds([[location.latitude, location.longitude]]), method: 'flyTo' };
  }
  if (polygon) {
    return { bounds: geoJSON(polygon).getBounds(), padding: 0.1, method: 'fitBounds' };
  }
  return undefined;
}

function getInvertedMunicipalityPolygon(polygon: GeoJSON.MultiPolygon): GeoJSON.MultiPolygon | undefined {
  if (!polygon || !polygon.coordinates) return polygon;

  const world: Position[] = [
    [90, 180],
    [90, -180],
    [-90, -180],
    [-90, 180],
  ];

  const coordinatesWithOuterShape = cloneDeep(polygon.coordinates);
  coordinatesWithOuterShape[0].unshift(world); // https://leafletjs.com/reference.html#polygon

  const invertedPolygon: GeoJSON.MultiPolygon = { type: 'MultiPolygon', coordinates: coordinatesWithOuterShape };

  return invertedPolygon;
}

function locationIsWithinMunicipality(item: ILocation, polygon: MultiPolygon): boolean {
  if (!item.latitude || !item.longitude) return false;

  // GeoJSON uses [longitude, latitude] order
  const pt = point([item.longitude, item.latitude]);

  return booleanPointInPolygon(pt, polygon);
}

function LocalServiceMap({ settings }: ILocalServiceMapProps) {
  // === STATE ===
  const [{ status: getLocalServiceGeoJsonStatus, data: getLocalServiceGeoJsonData }] = useFetch<GeoJsonObject[]>(`/api/local-service/excluded-areas/${settings.municipalityGid}`);
  const [{ status: getMunicipalityGeoJsonStatus, data: getMunicipalityGeoJsonData }] = useFetch<GeoJSON.MultiPolygon>(`/api/local-service/municipality/${settings.municipalityGid}`);
  const [{ data: getStopAreasData }] = useFetch<StopAreaApiModel[]>(`/api/local-service/stopareas/${settings.municipalityGid}`);
  const [locationAutocompleteState, setLocationAutocompleteState] = useState<ILocationAutocompleteState>({
    value: undefined,
    validationInfo: new ValidationInfo(),
  });
  const [currentLocation, setCurrentLocation] = useState<ILocation | undefined>(undefined);

  // === VARIABLES ===
  const stopAreas = processReceivedStopAreaData(getStopAreasData);
  const isLocationWithinMunicipality = currentLocation && getMunicipalityGeoJsonData && locationIsWithinMunicipality(currentLocation, getMunicipalityGeoJsonData as MultiPolygon);
  const boundsLocation = currentLocation && isLocationWithinMunicipality ? currentLocation : undefined;
  const bounds = useMemo(() => getBounds(boundsLocation, getMunicipalityGeoJsonData), [boundsLocation, getMunicipalityGeoJsonData]);

  const geoJsons =
    getLocalServiceGeoJsonData && getMunicipalityGeoJsonData && getLocalServiceGeoJsonStatus === Status.success && getMunicipalityGeoJsonStatus === Status.success
      ? getLocalServiceGeoJsonData
          .map((geoJsonData, index) => ({
            id: `local-service-${index}`,
            data: geoJsonData,
            options: { color: Colors.vtdBlue600, fillColor: Colors.vtdBlue600, fillOpacity: 0.3, weight: 2, interactive: false },
          }))
          .concat({
            id: 'municipality',
            data: getInvertedMunicipalityPolygon(getMunicipalityGeoJsonData) as GeoJsonObject,
            options: { color: Colors.vtdBlue600, fillColor: Colors.vtdBlack, fillOpacity: 0.3, weight: 2, interactive: false },
          })
      : undefined;

  async function handleLocationSelect(item: ILocationAutocompleteSelection | undefined): Promise<void> {
    if (item === undefined || item.type !== LocationAutocompleteSelectionType.location) return;
    setCurrentLocation(item.location);
    setLocationAutocompleteState({
      value: undefined,
      validationInfo: new ValidationInfo(),
    });
  }

  return (
    <div>
      <div className="d-print-none">
        <LocationAutocomplete
          id="local-service-map-autocomplete"
          className="mb-2"
          locationTypes={[LocationType.address]}
          state={locationAutocompleteState}
          labelText={t('Sök efter adress', 'Search address')}
          labelClassName="form-label"
          labelPosition={AutocompleteLabelPosition.above}
          placeholderText={t('Adress', 'Address')}
          enableCurrentPosition={false}
          ariaDescribedBy="local-service-map-autocomplete-description"
          onSelect={handleLocationSelect}
        />
      </div>
      <small id="local-service-map-autocomplete-description" className="d-block mb-3 d-print-none">
        {t('Sök på fullständig adress i vald kommun.', 'Search by full address in the selected municipality.')}
      </small>
      <div aria-live="polite">
        {currentLocation && (
          <div className="mb-3 bg-blue p-3">
            {!isLocationWithinMunicipality && (
              <span>
                {currentLocation.name} {t('ligger inte i vald kommun.', 'is not within the selected municipality.')}
              </span>
            )}
            {isLocationWithinMunicipality && (
              <div>
                <div className="mb-2">
                  <img src={DataUrlHelper.encode(localServiceMapIcon)} alt="" className="d-inline-block me-2" />
                  {currentLocation.name}
                </div>
                {currentLocation.hasLocalService && (
                  <div>
                    <div className="fs-2 fw-bold mb-2">{t('Adressen täcks av närtrafik', 'The address is served by local services')}</div>
                    <div>{t('Du kan resa från eller till någon av hållplatserna i kommunen.', 'You can travel to or from any of the stops within the municipality.')}</div>
                  </div>
                )}
                {!currentLocation.hasLocalService && (
                  <div>
                    <div className="fs-2 fw-bold mb-2 d-flex">
                      <img alt="" className="me-1" src={DataUrlHelper.encode(exclamationCircleRedIcon)} />
                      {t('Adressen täcks inte av närtrafik', 'The address is not served by local services')}
                    </div>
                    <div>
                      {t(
                        'Du kan inte resa från den här adressen, men du kan resa från eller till någon av hållplatserna i kommunen.',
                        'You cannot travel from this address, but you can travel to or from any of the stops within the municipality.',
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="print-no-break">
      <dl className="d-flex flex-wrap gap-2 flex-column flex-md-row">
        <div className="d-flex mb-1">
          <dt className="local-service-map__description-term local-service-map__description-term--area-with-local-services">
            <div className="w-100 h-100"></div>
          </dt>
          <dd className="ms-1 mb-0">{t('Område med närtrafik', 'Area with Local Services')}</dd>
        </div>
        <div className="d-flex mb-1">
          <dt className="local-service-map__description-term local-service-map__description-term--area-without-local-services">
            <div className="w-100 h-100"></div>
          </dt>
          <dd className="ms-1 mb-0">{t('Område utan närtrafik', 'Area without Local Services')}</dd>
        </div>
        <div className="d-flex">
          <dt className="local-service-map__description-term d-flex align-items-center justify-content-center">
            <img src={DataUrlHelper.encode(stopAreaIcon)} alt="" className="d-inline-block w-100" />
          </dt>
          <dd className="ms-1 mb-0">{t('Närtrafikhållplats', 'Local Services stops')}</dd>
        </div>
      </dl>
      <Map
        ariaLabel={t('Närtrafikkarta', 'Local Services map')}
        className="local-service-map__map"
        geoJsons={geoJsons}
        bounds={bounds}
        markers={stopAreas
          ?.flatMap((x) => x.stopPoints)
          .filter((y) => y.isFlexibleTaxiService)
          .map((x) =>
            x.latitude !== undefined && x.longitude !== undefined ? (
              <Marker
                position={[x.latitude, x.longitude]}
                key={x.gid}
                icon={
                  new ButtonDivIcon({
                    className: 'button-div-icon stop-area-marker',
                    iconSize: [34, 55],
                    iconAnchor: [17, 55],
                    popupAnchor: [0, 0],
                    html: `<img src="/icons/leaflet/map-marker-vasttrafik-blue-1000.svg" alt="${t.stopArea} ${SanitizerService.sanitizeHtml(x.name)}" class="w-100 h-100" />`,
                  })
                }
              >
                <Popup offset={[0, -55]}>{`${x.name}, ${t.platform} ${x.platform}`}</Popup>
              </Marker>
            ) : null,
          )
          .filter((marker): marker is React.ReactElement => marker !== null)
          .concat(
            currentLocation && isLocationWithinMunicipality && currentLocation.latitude && currentLocation.longitude
              ? [
                  <Marker
                    position={[currentLocation.latitude, currentLocation.longitude]}
                    key={currentLocation.name}
                    zIndexOffset={500}
                    icon={
                      new ButtonDivIcon({
                        className: 'div-icon stop-point-marker',
                        iconSize: [24, 34],
                        iconAnchor: [12, 34],
                        html: `<div class="position-relative">
                         <span class="visually-hidden">${SanitizerService.sanitizeHtml(currentLocation.name ?? '')}</span>
                         <img src="${DataUrlHelper.encode(localServiceMapIcon)}" alt="" class="w-100 h-100" />
                       </div>`,
                      })
                    }
                  >
                    <Popup offset={[0, -34]}>{currentLocation.name}</Popup>
                  </Marker>,
                ]
              : [],
          )}
      />
      </div>
    </div>
  );
}

export default LocalServiceMap;
